/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* HustleEats Phase 2.D — Floor plan + table joining + diner intel tab.
   Lets a venue admin:
   - Define their floor plan (table positions)
   - Join two tables for a big party
   - Look up a diner's seating preferences (staff-only intel)
*/
export default function FloorPlanTab({ venueSlug }) {
  const [plan, setPlan] = useState(null);
  const [tables, setTables] = useState([]);
  const [intelLookup, setIntelLookup] = useState({ user_id: "", result: null });
  const [intelEdit, setIntelEdit] = useState({
    diner_user_id: "",
    preferred_seat: "",
    notes: "",
    dietary: "",
    usual_order: "",
  });
  const [msg, setMsg] = useState(null);

  const refresh = async () => {
    const [plRes, tbRes] = await Promise.all([
      axios.get(`${API}/api/hustleeats/floor/${venueSlug}`, { withCredentials: true }),
      axios.get(`${API}/api/hustleeats/dining/tables/${venueSlug}`, { withCredentials: true }),
    ]);
    setPlan(plRes.data);
    setTables(tbRes.data || []);
  };

  useEffect(() => { refresh(); }, [venueSlug]);

  const joinTables = async (primaryId, otherId) => {
    setMsg(null);
    try {
      await axios.post(
        `${API}/api/hustleeats/floor/${venueSlug}/join`,
        { primary_table_id: primaryId, join_with_table_ids: [otherId] },
        { withCredentials: true },
      );
      setMsg({ kind: "ok", text: "Tables joined." });
      refresh();
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    }
  };

  const unjoin = async (tableId) => {
    await axios.post(
      `${API}/api/hustleeats/floor/${venueSlug}/unjoin/${tableId}`,
      {},
      { withCredentials: true },
    );
    refresh();
  };

  const lookupIntel = async () => {
    if (!intelLookup.user_id) return;
    setIntelLookup({ ...intelLookup, result: null });
    try {
      const { data } = await axios.get(
        `${API}/api/hustleeats/floor/intel/${venueSlug}/${encodeURIComponent(intelLookup.user_id)}`,
        { withCredentials: true },
      );
      setIntelLookup({ ...intelLookup, result: data });
    } catch (e) {
      setIntelLookup({ ...intelLookup, result: { error: e?.response?.data?.detail || "lookup failed" } });
    }
  };

  const saveIntel = async (e) => {
    e.preventDefault();
    setMsg(null);
    try {
      await axios.put(
        `${API}/api/hustleeats/floor/intel`,
        {
          venue_slug: venueSlug,
          diner_user_id: intelEdit.diner_user_id,
          preferred_seat: intelEdit.preferred_seat,
          notes: intelEdit.notes,
          dietary: intelEdit.dietary.split(",").map((s) => s.trim()).filter(Boolean),
          usual_order: intelEdit.usual_order,
        },
        { withCredentials: true },
      );
      setMsg({ kind: "ok", text: "Diner intel saved." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Failed." });
    }
  };

  return (
    <div className="ph2-tabbody">
      <h3 className="ph2-h3">Tables at this venue</h3>
      <div className="ph2-tables">
        {tables.length === 0 && <div className="he-state">No tables yet — add some on the Tables tab.</div>}
        {tables.map((t) => (
          <div key={t.id} className="ph2-table" data-testid={`fp-table-${t.id}`}>
            <div className="ph2-table__num">{t.table_number}</div>
            <div className="ph2-table__cap">cap {t.capacity}{t.joined_capacity ? ` → ${t.joined_capacity}` : ""}</div>
            {(t.joined_with || []).length > 0 ? (
              <button className="ph2-btn ph2-btn--ghost" onClick={() => unjoin(t.id)}>
                Unjoin
              </button>
            ) : (
              <select
                className="ph2-select"
                onChange={(e) => e.target.value && joinTables(t.id, e.target.value)}
                defaultValue=""
                data-testid={`fp-join-${t.id}`}
              >
                <option value="">Join with…</option>
                {tables.filter((o) => o.id !== t.id && !(o.joined_with || []).length).map((o) => (
                  <option key={o.id} value={o.id}>{o.table_number}</option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>

      <h3 className="ph2-h3" style={{ marginTop: 32 }}>Diner intel (staff-only)</h3>
      <p className="he-admin__hint">
        Look up what your team knows about a diner — preferred seat, dietary, usual order. Never shown to the diner.
      </p>

      <div className="ph2-row">
        <div className="ph2-col">
          <h4 className="ph2-h4">Look up</h4>
          <div className="he-form__row">
            <label className="he-form__field">
              <span>Diner user id (email)</span>
              <input
                type="text"
                value={intelLookup.user_id}
                onChange={(e) => setIntelLookup({ ...intelLookup, user_id: e.target.value })}
                data-testid="fp-intel-lookup-input"
              />
            </label>
            <button type="button" className="he-card__cta" onClick={lookupIntel} data-testid="fp-intel-lookup-btn">
              Look up
            </button>
          </div>
          {intelLookup.result && !intelLookup.result.error && (
            <div className="ph2-intel-card" data-testid="fp-intel-card">
              <div className="ph2-intel__visits"><strong>{intelLookup.result.visit_count}</strong> visit{intelLookup.result.visit_count === 1 ? "" : "s"}</div>
              <div><strong>Preferred seat:</strong> {intelLookup.result.preferred_seat || "—"}</div>
              <div><strong>Dietary:</strong> {(intelLookup.result.dietary || []).join(", ") || "—"}</div>
              <div><strong>Usual order:</strong> {intelLookup.result.usual_order || "—"}</div>
              <div><strong>Notes:</strong> {intelLookup.result.notes || "—"}</div>
            </div>
          )}
          {intelLookup.result?.error && (
            <div className="he-admin__msg he-admin__msg--err">{intelLookup.result.error}</div>
          )}
        </div>

        <div className="ph2-col">
          <h4 className="ph2-h4">Save / update</h4>
          <form onSubmit={saveIntel} className="he-form">
            <label className="he-form__field">
              <span>Diner user id (email)</span>
              <input
                type="text"
                value={intelEdit.diner_user_id}
                onChange={(e) => setIntelEdit({ ...intelEdit, diner_user_id: e.target.value })}
                required
                data-testid="fp-intel-edit-id"
              />
            </label>
            <label className="he-form__field">
              <span>Preferred seat</span>
              <input
                type="text"
                value={intelEdit.preferred_seat}
                onChange={(e) => setIntelEdit({ ...intelEdit, preferred_seat: e.target.value })}
                placeholder="e.g. Corner booth #7"
                data-testid="fp-intel-edit-seat"
              />
            </label>
            <label className="he-form__field">
              <span>Dietary (comma-separated)</span>
              <input
                type="text"
                value={intelEdit.dietary}
                onChange={(e) => setIntelEdit({ ...intelEdit, dietary: e.target.value })}
                placeholder="gluten-free, no shellfish"
              />
            </label>
            <label className="he-form__field">
              <span>Usual order</span>
              <input
                type="text"
                value={intelEdit.usual_order}
                onChange={(e) => setIntelEdit({ ...intelEdit, usual_order: e.target.value })}
              />
            </label>
            <label className="he-form__field">
              <span>Notes</span>
              <textarea
                rows={2}
                value={intelEdit.notes}
                onChange={(e) => setIntelEdit({ ...intelEdit, notes: e.target.value })}
              />
            </label>
            <button type="submit" className="he-card__cta" data-testid="fp-intel-save-btn">
              Save intel
            </button>
          </form>
        </div>
      </div>

      {msg && <div className={`he-admin__msg he-admin__msg--${msg.kind}`}>{msg.text}</div>}
    </div>
  );
}
